import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Search, Star, Shirt, Users, Baby, Ruler, Palette, Tag, Wallet } from 'lucide-react';
import './index.css';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'unisex', label: 'Unisex' },
];

// A handful of skeleton rows shown in place of a facet's real options
// while that data is still loading — same visual footprint as a real
// .filter-checkbox-label row, so nothing jumps in size once the real
// content replaces it.
const FacetSkeleton = ({ count = 3 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="filter-checkbox-skeleton" />
    ))}
  </>
);

const ApparelFilters = ({
  filters,
  setFilters,
  menuFilterSettings = {},
  showGenderFilter = false,
  availableBrands = [],
  availableColors = [],
  availableAgeGroups = [],
  availableSizes = [],
  availableCategories = [],
  // True while categories/products for this store are still being
  // fetched — see RestaurantMenuApp's categoryLoading. Categories,
  // Gender, Age Group, Color, and Brand all ultimately depend on that
  // same fetch, so without this every one of them either shows nothing
  // or (for Gender/Age/Color/Brand, which are gated on having at least
  // one real value) doesn't render at all until it resolves — the
  // "empty sidebar, then everything pops in" effect this prop fixes.
  isLoading = false
}) => {
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    price: true,
    brands: true,
    colors: true,
    sizes: true,
    gender: true,
    ageGroups: true,
    ratings: true
  });

  const [brandSearch, setBrandSearch] = useState('');
  const [showAllBrands, setShowAllBrands] = useState(false);
  const BRAND_PREVIEW_COUNT = 6;

  // Custom scrollbar thumb — same reasoning and pattern as the Restaurant
  // Menu App's Categories sidebar (RestaurantMenuApp/index.js): no browser
  // lets CSS give a native scrollbar thumb a fixed/short height (confirmed
  // by direct testing), since it's always sized proportionally to visible
  // vs. total content. Native scrolling (wheel/trackpad/touch) is
  // completely unaffected — .apparel-filters-sidebar-scroll below is still
  // the real, natively-scrolling element; only its default scrollbar
  // chrome is hidden in favor of this custom indicator drawn on top of it.
  const filtersScrollRef = useRef(null);
  const filtersThumbRef = useRef(null);
  const filtersTrackRef = useRef(null);
  const FILTERS_THUMB_HEIGHT = 48;

  useEffect(() => {
    const el = filtersScrollRef.current;
    const thumb = filtersThumbRef.current;
    const track = filtersTrackRef.current;
    if (!el || !thumb || !track) return undefined;
    let hideTimer;

    const updateThumb = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScrollTop = scrollHeight - clientHeight;
      if (maxScrollTop <= 1) {
        thumb.style.display = 'none';
        return;
      }
      thumb.style.display = 'block';
      const trackHeight = track.clientHeight;
      const thumbHeight = Math.min(FILTERS_THUMB_HEIGHT, trackHeight);
      const travel = Math.max(trackHeight - thumbHeight, 0);
      const thumbTop = (scrollTop / maxScrollTop) * travel;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
    };

    // Thumb stays hidden until scroll activity, then fades out again after
    // a short idle period — matches OS-style auto-hide scrollbars. Dragging
    // the thumb itself also fires native scroll events (see the pointerdown
    // handler below setting el.scrollTop), so this same listener keeps it
    // visible through a drag too, without needing separate logic there.
    const handleScroll = () => {
      updateThumb();
      thumb.classList.add('is-visible');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => thumb.classList.remove('is-visible'), 1000);
    };

    updateThumb();
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateThumb);

    // Filter section content height can change after this effect's
    // initial run (expanding/collapsing sections, brand list growing) — a
    // ResizeObserver catches that without needing every state value that
    // could affect it as a dep.
    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThumb);
      resizeObserver.disconnect();
      clearTimeout(hideTimer);
    };
  }, [availableCategories, availableBrands, availableColors, availableAgeGroups, availableSizes]);

  const handleFiltersThumbPointerDown = (e) => {
    const el = filtersScrollRef.current;
    const track = filtersTrackRef.current;
    if (!el || !track) return;
    e.preventDefault();
    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.min(FILTERS_THUMB_HEIGHT, trackHeight);
    const travel = Math.max(trackHeight - thumbHeight, 0);

    const handlePointerMove = (moveEvent) => {
      if (travel <= 0) return;
      const deltaY = moveEvent.clientY - startY;
      const scrollDelta = (deltaY / travel) * maxScrollTop;
      el.scrollTop = Math.min(Math.max(startScrollTop + scrollDelta, 0), maxScrollTop);
    };
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCheckboxChange = (filterCategory, value) => {
    setFilters(prev => {
      const current = prev[filterCategory] || [];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      
      return {
        ...prev,
        [filterCategory]: updated
      };
    });
  };

  const handleRadioChange = (filterCategory, value) => {
    setFilters(prev => ({
      ...prev,
      [filterCategory]: prev[filterCategory] === value ? null : value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      categories: [],
      priceRange: null,
      brands: [],
      colors: [],
      sizes: [],
      gender: [],
      ageGroups: [],
      minRating: null
    });
  };

  const filteredBrands = availableBrands.filter(b =>
    b.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const activeFilterCount =
    (filters.categories?.length || 0) +
    (filters.priceRange ? 1 : 0) +
    (filters.gender?.length || 0) +
    (filters.ageGroups?.length || 0) +
    (filters.sizes?.length || 0) +
    (filters.colors?.length || 0) +
    (filters.brands?.length || 0) +
    (filters.minRating ? 1 : 0);

  return (
    <div className="apparel-filters-sidebar">
      <div className="apparel-filters-sidebar-scroll" ref={filtersScrollRef}>
      <div className="filters-header">
        <h3 className="filters-title">
          Filters
          {activeFilterCount > 0 && <span className="filters-count-badge">{activeFilterCount}</span>}
        </h3>
        <button className="clear-all-btn" onClick={clearAllFilters}>Clear All</button>
      </div>

      {/* CATEGORIES */}
      <div className="filter-section">
        <div className="filter-section-header" onClick={() => toggleSection('categories')}>
          <h4><Shirt size={15} className="filter-section-icon" /> Categories</h4>
          {expandedSections.categories ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {expandedSections.categories && (
          <div className="filter-section-content">
            {availableCategories.length === 0 && isLoading ? (
              <FacetSkeleton count={6} />
            ) : (
              availableCategories.map(cat => (
                <label
                  key={cat.id}
                  className={`filter-checkbox-label ${(filters.categories || []).includes(cat.id) ? 'is-checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={(filters.categories || []).includes(cat.id)}
                    onChange={() => handleCheckboxChange('categories', cat.id)}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">{cat.name}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>

      {/* PRICE RANGE */}
      {(menuFilterSettings.price || menuFilterSettings.price_range) && (
        <div className="filter-section">
          <div className="filter-section-header" onClick={() => toggleSection('price')}>
            <h4><Wallet size={15} className="filter-section-icon" /> Price</h4>
            {expandedSections.price ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {expandedSections.price && (
            <div className="filter-section-content">
              {[
                { id: '0-500', label: 'Under ₹500' },
                { id: '500-1000', label: '₹500 - ₹1000' },
                { id: '1000-2500', label: '₹1000 - ₹2500' },
                { id: '2500-5000', label: '₹2500 - ₹5000' },
                { id: '5000+', label: 'Over ₹5000' }
              ].map(range => (
                <label key={range.id} className="filter-radio-label">
                  <input 
                    type="radio" 
                    name="priceRange"
                    checked={filters.priceRange === range.id}
                    onChange={() => handleRadioChange('priceRange', range.id)}
                  />
                  <span className="radio-custom"></span>
                  <span className="radio-text">{range.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* GENDER — normally only shown when there are 2+ distinct values
          present (a single-value filter can't narrow anything down), but
          that check needs product data that isn't in yet on first load —
          shown with a skeleton in the meantime whenever the store's own
          config says Gender is a filter it offers, so the section doesn't
          just pop into existence once loading finishes. */}
      {(showGenderFilter || (menuFilterSettings.gender && isLoading)) && (
        <div className="filter-section">
          <div className="filter-section-header" onClick={() => toggleSection('gender')}>
            <h4><Users size={15} className="filter-section-icon" /> Gender</h4>
            {expandedSections.gender ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {expandedSections.gender && (
            <div className="filter-section-content">
              {!showGenderFilter && isLoading ? (
                <FacetSkeleton count={3} />
              ) : (
                GENDER_OPTIONS.map(({ value, label }) => (
                  <label
                    key={value}
                    className={`filter-checkbox-label ${filters.gender?.includes(value) ? 'is-checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={filters.gender?.includes(value) || false}
                      onChange={() => handleCheckboxChange('gender', value)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">{label}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* AGE GROUP — same loading/skeleton reasoning as Gender above. */}
      {menuFilterSettings.age_group && (availableAgeGroups.length > 0 || isLoading) && (
        <div className="filter-section">
          <div className="filter-section-header" onClick={() => toggleSection('ageGroups')}>
            <h4><Baby size={15} className="filter-section-icon" /> Age Group</h4>
            {expandedSections.ageGroups ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {expandedSections.ageGroups && (
            <div className="filter-section-content">
              {availableAgeGroups.length === 0 && isLoading ? (
                <FacetSkeleton count={3} />
              ) : (
                availableAgeGroups.map(ageGroup => (
                  <label
                    key={ageGroup}
                    className={`filter-checkbox-label ${filters.ageGroups?.includes(ageGroup) ? 'is-checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={filters.ageGroups?.includes(ageGroup) || false}
                      onChange={() => handleCheckboxChange('ageGroups', ageGroup)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">{ageGroup.charAt(0).toUpperCase() + ageGroup.slice(1)}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* SIZES */}
      {(menuFilterSettings.size || menuFilterSettings.sizes) && availableSizes.length > 0 && (
        <div className="filter-section">
          <div className="filter-section-header" onClick={() => toggleSection('sizes')}>
            <h4><Ruler size={15} className="filter-section-icon" /> Size</h4>
            {expandedSections.sizes ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {expandedSections.sizes && (
            <div className="filter-section-content size-grid">
              {availableSizes.map(size => (
                <button
                  key={size}
                  className={`size-box ${filters.sizes?.includes(size) ? 'active' : ''}`}
                  onClick={() => handleCheckboxChange('sizes', size)}
                >
                  {size}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* COLORS — same loading/skeleton reasoning as Gender above. */}
      {menuFilterSettings.color && (availableColors.length > 0 || isLoading) && (
        <div className="filter-section">
          <div className="filter-section-header" onClick={() => toggleSection('colors')}>
            <h4><Palette size={15} className="filter-section-icon" /> Color</h4>
            {expandedSections.colors ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {expandedSections.colors && (
            <div className="filter-section-content">
              {availableColors.length === 0 && isLoading ? (
                <FacetSkeleton count={5} />
              ) : (
                availableColors.map(color => (
                  <label
                    key={color}
                    className={`filter-checkbox-label ${filters.colors?.includes(color) ? 'is-checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={filters.colors?.includes(color) || false}
                      onChange={() => handleCheckboxChange('colors', color)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">{color}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* BRANDS — same loading/skeleton reasoning as Gender above; search
          box and View More are held back until there's real data to
          search/expand. */}
      {menuFilterSettings.brand && (availableBrands.length > 0 || isLoading) && (
        <div className="filter-section">
          <div className="filter-section-header" onClick={() => toggleSection('brands')}>
            <h4><Tag size={15} className="filter-section-icon" /> Brand</h4>
            {expandedSections.brands ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {expandedSections.brands && (
            <div className="filter-section-content">
              {availableBrands.length === 0 && isLoading ? (
                <FacetSkeleton count={5} />
              ) : (
                <>
                  <div className="filter-search-box">
                    <Search size={14} className="filter-search-icon" />
                    <input
                      type="text"
                      placeholder="Search brands"
                      value={brandSearch}
                      onChange={(e) => setBrandSearch(e.target.value)}
                    />
                  </div>
                  <div className="filter-list">
                    {(showAllBrands ? filteredBrands : filteredBrands.slice(0, BRAND_PREVIEW_COUNT)).map(brand => (
                      <label
                        key={brand}
                        className={`filter-checkbox-label ${(filters.brands || []).includes(brand) ? 'is-checked' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={(filters.brands || []).includes(brand)}
                          onChange={() => handleCheckboxChange('brands', brand)}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">{brand}</span>
                      </label>
                    ))}
                  </div>
                  {filteredBrands.length > BRAND_PREVIEW_COUNT && (
                    <button
                      type="button"
                      className="filter-show-more-link"
                      onClick={() => setShowAllBrands(prev => !prev)}
                    >
                      {showAllBrands ? 'View Less' : 'View More'}
                      {showAllBrands ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* RATINGS */}
      {(menuFilterSettings.ratings || menuFilterSettings.customer_ratings) && (
        <div className="filter-section border-bottom-0">
          <div className="filter-section-header" onClick={() => toggleSection('ratings')}>
            <h4><Star size={15} className="filter-section-icon" /> Customer Ratings</h4>
            {expandedSections.ratings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {expandedSections.ratings && (
            <div className="filter-section-content">
              {[4, 3, 2, 1].map(rating => (
                <label key={rating} className="filter-radio-label">
                  <input 
                    type="radio" 
                    name="minRating"
                    checked={filters.minRating === rating}
                    onChange={() => handleRadioChange('minRating', rating)}
                  />
                  <span className="radio-custom"></span>
                  <span className="radio-text rating-stars">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} fill={i < rating ? "#FFD700" : "none"} color={i < rating ? "#FFD700" : "#D1D5DB"} />
                    ))}
                    <span className="rating-up">& Up</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      </div>
      <div className="apparel-filters-scrollbar-track" ref={filtersTrackRef} aria-hidden="true">
        <div
          className="apparel-filters-scrollbar-thumb"
          ref={filtersThumbRef}
          onPointerDown={handleFiltersThumbPointerDown}
        />
      </div>
    </div>
  );
};

export default ApparelFilters;
